import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { database } from '../../config/backend';
import { ref, get as rtdbGet, set as rtdbSet, push, query, orderByChild, equalTo } from '../../services/phpDataAdapter';
import { searchFoods, FoodItem, getFoodById, createCustomFood } from '../../services/foodDatabaseService';
import './Plan.css';

interface SavedMeal {
  id: string;
  name: string;
  foods: Array<{
    foodId: string;
    foodName: string;
    quantity: number;
    unit: string;
  }>;
  totalCalories: number;
  totalProtein: number;
  totalCarbs: number;
  totalFats: number;
  isFavorite: boolean;
  createdAt: string;
}

type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
type MealKey = 'breakfast' | 'morningSnack' | 'lunch' | 'afternoonSnack' | 'dinner' | 'eveningSnack';

interface MealPlanHelperProps {
  onAddMeal: (meal: SavedMeal) => void;
  onAddFood: (food: FoodItem, quantity: number) => void;
  onAddFoodToMeal?: (food: FoodItem, day: DayKey, meal: MealKey, quantity: number) => void;
}

const CATEGORIES = [
  { id: 'all', label: 'Todos' },
  { id: 'protein', label: 'Proteínas' },
  { id: 'grains', label: 'Grãos e Cereais' },
  { id: 'carbs', label: 'Carboidratos' },
  { id: 'vegetables', label: 'Vegetais' },
  { id: 'fruits', label: 'Frutas' },
  { id: 'dairy', label: 'Laticínios' }
];

const DAY_LABELS: Record<DayKey, string> = {
  monday: 'Segunda-feira',
  tuesday: 'Terça-feira',
  wednesday: 'Quarta-feira',
  thursday: 'Quinta-feira',
  friday: 'Sexta-feira',
  saturday: 'Sábado',
  sunday: 'Domingo',
};

const MEAL_LABELS: Record<MealKey, string> = {
  breakfast: 'Café da Manhã',
  morningSnack: 'Lanche da Manhã',
  lunch: 'Almoço',
  afternoonSnack: 'Lanche da Tarde',
  dinner: 'Jantar',
  eveningSnack: 'Ceia',
};

export const MealPlanHelper: React.FC<MealPlanHelperProps> = ({ onAddMeal, onAddFood, onAddFoodToMeal }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'foods' | 'meals' | 'create'>('foods');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [savedMeals, setSavedMeals] = useState<SavedMeal[]>([]);
  const [favoriteMeals, setFavoriteMeals] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [displayFoods, setDisplayFoods] = useState<FoodItem[]>([]);
  const [selectedFoods, setSelectedFoods] = useState<Array<{ food: FoodItem; quantity: number }>>([]);
  const [newMealName, setNewMealName] = useState('');
  const [showCreateMealModal, setShowCreateMealModal] = useState(false);
  const [showAddCustomFoodModal, setShowAddCustomFoodModal] = useState(false);
  const [customFoods, setCustomFoods] = useState<FoodItem[]>([]);
  const [newCustomFood, setNewCustomFood] = useState({
    name: '',
    category: 'other',
    servingSize: '100',
    servingUnit: 'g',
    calories: '',
    protein: '',
    carbs: '',
    fats: '',
    barcode: ''
  });
  const [showMealSelectorModal, setShowMealSelectorModal] = useState(false);
  const [selectedFoodForMeal, setSelectedFoodForMeal] = useState<FoodItem | null>(null);
  const [selectedDay, setSelectedDay] = useState<DayKey>('monday');
  const [selectedMeal, setSelectedMeal] = useState<MealKey>('breakfast');
  const [selectedQuantity, setSelectedQuantity] = useState<string>('');

  useEffect(() => {
    if (isOpen && user) {
      loadSavedMeals();
    }
  }, [isOpen, user]);

  // Efeito para busca dinâmica de alimentos
  useEffect(() => {
    let active = true;
    const fetchFoods = async () => {
      if (!isOpen || activeTab !== 'foods') return;

      setSearching(true);
      setSearchError('');
      try {
        const category = selectedCategory !== 'all' ? (selectedCategory as FoodItem['category']) : undefined;
        const term = searchTerm.trim();

        if (term.length === 1 && !category) {
          setDisplayFoods([]);
          return;
        }

        if (category || term.length >= 2) {
          const searchQuery = term.length >= 2 ? term : '';
          console.log('Buscando alimentos:', { searchQuery, category });
          const results = await searchFoods(searchQuery, category, 50);
          if (!active) return;
          console.log('Resultados encontrados:', results.length);
          setDisplayFoods(results);
        } else {
          const results = await searchFoods('', undefined, 20);
          if (!active) return;
          setDisplayFoods(results);
        }
      } catch (error) {
        console.error('Erro ao buscar alimentos:', error);
        if (active) {
          setSearchError(error instanceof Error
            ? error.message
            : 'Não foi possível consultar a tabela de alimentos.');
          setDisplayFoods([]);
        }
      } finally {
        if (active) setSearching(false);
      }
    };

    const debounceTimer = window.setTimeout(fetchFoods, 300);
    return () => {
      active = false;
      window.clearTimeout(debounceTimer);
    };
  }, [searchTerm, selectedCategory, isOpen, activeTab]);

  const loadSavedMeals = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const mealsRef = ref(database, `users/${user.uid}/savedMeals`);
      const snapshot = await rtdbGet(mealsRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        const meals: SavedMeal[] = Object.entries(data).map(([id, meal]: [string, any]) => ({
          id,
          ...meal
        }));
        setSavedMeals(meals);
        const favorites = new Set(meals.filter(m => m.isFavorite).map(m => m.id));
        setFavoriteMeals(favorites);
      }
    } catch (error) {
      console.error('Erro ao carregar refeições salvas:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (mealId: string) => {
    if (!user?.uid) return;
    const isFavorite = favoriteMeals.has(mealId);
    const newFavorites = new Set(favoriteMeals);

    if (isFavorite) {
      newFavorites.delete(mealId);
    } else {
      newFavorites.add(mealId);
    }

    setFavoriteMeals(newFavorites);

    try {
      await rtdbSet(ref(database, `users/${user.uid}/savedMeals/${mealId}/isFavorite`), !isFavorite);
    } catch (error) {
      console.error('Erro ao atualizar favorito:', error);
    }
  };

  const addFoodToSelection = (food: FoodItem) => {
    const existingIndex = selectedFoods.findIndex(sf => sf.food.id === food.id);
    if (existingIndex >= 0) {
      const updated = [...selectedFoods];
      updated[existingIndex].quantity += food.servingSize;
      setSelectedFoods(updated);
    } else {
      setSelectedFoods([...selectedFoods, { food, quantity: food.servingSize }]);
    }
  };

  const removeFoodFromSelection = (foodId: string) => {
    setSelectedFoods(selectedFoods.filter(sf => sf.food.id !== foodId));
  };

  const updateFoodQuantity = (foodId: string, quantity: number) => {
    setSelectedFoods(selectedFoods.map(sf =>
      sf.food.id === foodId ? { ...sf, quantity: Math.max(0, quantity) } : sf
    ));
  };

  const handleCreateMeal = async () => {
    if (!newMealName.trim() || selectedFoods.length === 0) {
      alert('Preencha o nome da refeição e adicione pelo menos um alimento');
      return;
    }

    const foods = selectedFoods.map(sf => ({
      foodId: sf.food.id,
      foodName: sf.food.name,
      quantity: sf.quantity,
      unit: sf.food.servingUnit
    }));

    const totals = {
      calories: selectedFoods.reduce((sum, sf) => sum + (sf.food.nutritionalInfo.calories * (sf.quantity / sf.food.servingSize)), 0),
      protein: selectedFoods.reduce((sum, sf) => sum + (sf.food.nutritionalInfo.protein * (sf.quantity / sf.food.servingSize)), 0),
      carbs: selectedFoods.reduce((sum, sf) => sum + (sf.food.nutritionalInfo.carbs * (sf.quantity / sf.food.servingSize)), 0),
      fats: selectedFoods.reduce((sum, sf) => sum + (sf.food.nutritionalInfo.fats * (sf.quantity / sf.food.servingSize)), 0),
    };

    await saveMeal(newMealName.trim(), foods, totals);
    setSelectedFoods([]);
    setNewMealName('');
    setShowCreateMealModal(false);
    setActiveTab('meals');
  };

  const saveMeal = async (
    name: string,
    foods: Array<{ foodId: string; foodName: string; quantity: number; unit: string }>,
    totals: { calories: number; protein: number; carbs: number; fats: number }
  ) => {
    if (!user?.uid || !name || foods.length === 0) return;

    const meal: Omit<SavedMeal, 'id'> = {
      name,
      foods,
      totalCalories: Math.round(totals.calories),
      totalProtein: Math.round(totals.protein * 10) / 10,
      totalCarbs: Math.round(totals.carbs * 10) / 10,
      totalFats: Math.round(totals.fats * 10) / 10,
      isFavorite: false,
      createdAt: new Date().toISOString()
    };

    try {
      const mealRef = push(ref(database, `users/${user.uid}/savedMeals`));
      await rtdbSet(mealRef, meal);
      await loadSavedMeals();
      alert('Refeição salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar refeição:', error);
      alert('Erro ao salvar refeição');
    }
  };

  const handleCreateCustomFood = async () => {
    if (!user?.uid) return;
    if (!newCustomFood.name || !newCustomFood.servingSize) {
      alert('Preencha pelo menos o nome e o tamanho da porção');
      return;
    }

    try {
      const food: Omit<FoodItem, 'id' | 'createdAt' | 'customFood' | 'verified'> = {
        name: newCustomFood.name,
        category: newCustomFood.category as any,
        servingSize: parseFloat(newCustomFood.servingSize),
        servingUnit: newCustomFood.servingUnit as any,
        nutritionalInfo: {
          calories: parseFloat(newCustomFood.calories) || 0,
          protein: parseFloat(newCustomFood.protein) || 0,
          carbs: parseFloat(newCustomFood.carbs) || 0,
          fats: parseFloat(newCustomFood.fats) || 0,
        },
        createdBy: user.uid,
        barcode: newCustomFood.barcode
      };

      await createCustomFood(user.uid, food);
      setNewCustomFood({
        name: '',
        category: 'other',
        servingSize: '100',
        servingUnit: 'g',
        calories: '',
        protein: '',
        carbs: '',
        fats: '',
        barcode: ''
      });
      setShowAddCustomFoodModal(false);

      // Atualizar lista se estivermos buscando
      if (activeTab === 'foods') {
        // Re-trigger search by resetting search term or similar?
        // searchFoods fetches internally. We can just setDisplayFoods maybe.
        // searchFoods also fetches custom foods from the PHP API.
        // Let's just trigger a re-search
        setSearchTerm(prev => prev + ' '); // Hacky trigger? No.
        // Just manual fetch
        const results = await searchFoods(searchTerm, selectedCategory !== 'all' ? selectedCategory as any : undefined, 50);
        setDisplayFoods(results);
      }

      alert('Alimento criado com sucesso!');
    } catch (error) {
      console.error('Erro ao criar alimento:', error);
      alert('Erro ao criar alimento');
    }
  };

  const filteredMeals = savedMeals.filter(meal => {
    return meal.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <>
      <Button variant="secondary" onClick={() => setIsOpen(true)} style={{ fontSize: '13px', padding: '8px 16px' }}>
        🍽️ Auxiliar de Refeições
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Auxiliar de Montagem de Planos Alimentares"
        footer={
          <Button variant="secondary" onClick={() => setIsOpen(false)}>
            Fechar
          </Button>
        }
      >
        <div className="ns-meal-plan-helper">
          <div className="ns-helper-tabs">
            <button
              type="button"
              className={activeTab === 'foods' ? 'active' : ''}
              onClick={() => setActiveTab('foods')}
            >
              🍎 Alimentos
            </button>
            <button
              type="button"
              className={activeTab === 'meals' ? 'active' : ''}
              onClick={() => setActiveTab('meals')}
            >
              🍽️ Refeições Salvas
            </button>
            <button
              type="button"
              className={activeTab === 'create' ? 'active' : ''}
              onClick={() => {
                setActiveTab('create');
                if (selectedFoods.length === 0) {
                  setShowCreateMealModal(true);
                }
              }}
            >
              ➕ Criar Refeição
            </button>
          </div>

          <div style={{ marginTop: '16px' }}>
            <input
              type="text"
              className="ns-input"
              placeholder="Buscar alimentos ou refeições..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', marginBottom: '12px' }}
            />

            {activeTab === 'foods' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', flex: 1 }}>
                    {CATEGORIES.map(cat => (
                      <button
                        key={cat.id}
                        type="button"
                        className={`ns-helper-category-button ${selectedCategory === cat.id ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(cat.id)}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => setShowAddCustomFoodModal(true)}
                    style={{ fontSize: '11px', padding: '6px 12px', whiteSpace: 'nowrap' }}
                  >
                    ➕ Adicionar Alimento
                  </Button>
                </div>

                {searching && (
                  <div className="ns-helper-search-status" role="status">
                    🔍 Buscando alimentos...
                  </div>
                )}

                {!searching && searchError && (
                  <div className="ns-helper-search-status ns-food-search-error" role="alert">
                    {searchError}
                  </div>
                )}

                {!searching && !searchError && displayFoods.length === 0 && (
                  <div className="ns-helper-search-status">
                    {searchTerm.length > 0
                      ? `Nenhum alimento encontrado para "${searchTerm}".`
                      : selectedCategory !== 'all'
                        ? `Nenhum alimento encontrado na categoria "${CATEGORIES.find(c => c.id === selectedCategory)?.label}".`
                        : 'Digite para buscar alimentos ou selecione uma categoria.'}
                  </div>
                )}

                {!searching && displayFoods.length > 0 && (
                  <div className="ns-food-results-count">
                    <div>
                      📊 {displayFoods.length} alimento{displayFoods.length !== 1 ? 's' : ''} encontrado{displayFoods.length !== 1 ? 's' : ''}
                      {displayFoods.filter(f => f.fromTaco).length > 0 && (
                        <span className="ns-food-taco-count">
                          {displayFoods.filter(f => f.fromTaco).length} da TACO 🇧🇷
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="ns-food-card-grid">
                  {!searching && displayFoods.map(food => (
                    <div
                      key={food.id}
                      className={`ns-food-card ${food.fromTaco ? 'from-taco' : ''}`}
                    >
                      <div className="ns-food-card-heading">
                        <div className="ns-food-card-name">{food.name}</div>
                        {food.fromTaco && (
                          <span className="ns-food-card-badge">
                            🇧🇷 TACO
                          </span>
                        )}
                      </div>
                      <div className="ns-food-card-serving">
                        📏 {food.servingSize} {food.servingUnit}
                      </div>
                      <div className="ns-food-card-nutrients">
                        <div>🔥 {food.nutritionalInfo.calories} kcal</div>
                        <div>🥩 {food.nutritionalInfo.protein}g</div>
                        <div>🍞 {food.nutritionalInfo.carbs}g</div>
                        <div>🧈 {food.nutritionalInfo.fats}g</div>
                      </div>
                      <div className="ns-food-card-actions">
                        <Button
                          variant="secondary"
                          onClick={() => {
                            if (onAddFoodToMeal) {
                              setSelectedFoodForMeal(food as FoodItem);
                              setSelectedQuantity(String(food.servingSize));
                              setShowMealSelectorModal(true);
                            } else {
                              const quantity = food.servingSize;
                              onAddFood(food as FoodItem, quantity);
                              alert(`✅ ${food.name} adicionado ao plano!`);
                            }
                          }}
                          className={`ns-food-card-add ${food.fromTaco ? 'from-taco' : ''}`}
                        >
                          ➕ Adicionar
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            addFoodToSelection(food as FoodItem);
                            setActiveTab('create');
                            setShowCreateMealModal(true);
                          }}
                        >
                          🍽️ Refeição
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {activeTab === 'create' && (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label className="ns-field">
                    <span>Nome da Refeição *</span>
                    <input
                      type="text"
                      className="ns-input"
                      placeholder="Ex: Café da Manhã Completo"
                      value={newMealName}
                      onChange={(e) => setNewMealName(e.target.value)}
                    />
                  </label>
                </div>

                {selectedFoods.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--page-text-muted)' }}>
                    <p>Nenhum alimento selecionado ainda.</p>
                    <p style={{ fontSize: '12px', marginTop: '8px' }}>
                      Vá para a aba "Alimentos" e clique em "Criar Refeição" nos alimentos desejados.
                    </p>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Alimentos Selecionados:</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {selectedFoods.map((sf, index) => {
                          const multiplier = sf.quantity / parseFloat(sf.food.servingSize as any); // Cast for safety if mismatch
                          const calories = sf.food.nutritionalInfo.calories * multiplier;
                          const protein = sf.food.nutritionalInfo.protein * multiplier;
                          const carbs = sf.food.nutritionalInfo.carbs * multiplier;
                          const fats = sf.food.nutritionalInfo.fats * multiplier;

                          return (
                            <div
                              key={sf.food.id}
                              style={{
                                padding: '12px',
                                background: 'var(--page-surface-hover)',
                                borderRadius: '8px',
                                border: '1px solid var(--page-border)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>
                                    {sf.food.name}
                                  </div>
                                  <div style={{ fontSize: '11px', color: 'var(--page-text-muted)' }}>
                                    {Math.round(calories)} kcal | P: {protein.toFixed(1)}g | C: {carbs.toFixed(1)}g | G: {fats.toFixed(1)}g
                                  </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={sf.quantity}
                                    onChange={(e) => updateFoodQuantity(sf.food.id, parseFloat(e.target.value) || 0)}
                                    style={{
                                      width: '70px',
                                      padding: '6px 8px',
                                      border: '1px solid var(--page-border)',
                                      borderRadius: '6px',
                                      fontSize: '12px'
                                    }}
                                  />
                                  <span style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>{sf.food.servingUnit}</span>
                                  <button
                                    type="button"
                                    onClick={() => removeFoodFromSelection(sf.food.id)}
                                    style={{
                                      padding: '4px 8px',
                                      background: '#ef4444',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px'
                                    }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>

                              {/* Micronutrientes Expandidos */}
                              {(sf.food.nutritionalInfo.vitaminC || sf.food.nutritionalInfo.calcium || sf.food.nutritionalInfo.iron || sf.food.nutritionalInfo.sodium) && (
                                <div style={{
                                  fontSize: '10px',
                                  color: 'var(--page-text-muted)',
                                  background: 'var(--page-surface)',
                                  padding: '6px',
                                  borderRadius: '4px',
                                  border: '1px dashed var(--page-border)',
                                  marginTop: '4px',
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                  gap: '4px'
                                }}>
                                  {sf.food.nutritionalInfo.sodium ? <span>🧂 Sódio: {(sf.food.nutritionalInfo.sodium * multiplier).toFixed(1)}mg</span> : null}
                                  {sf.food.nutritionalInfo.calcium ? <span>🦴 Cálcio: {(sf.food.nutritionalInfo.calcium * multiplier).toFixed(1)}mg</span> : null}
                                  {sf.food.nutritionalInfo.iron ? <span>🩸 Ferro: {(sf.food.nutritionalInfo.iron * multiplier).toFixed(1)}mg</span> : null}
                                  {sf.food.nutritionalInfo.vitaminC ? <span>🍊 Vit. C: {(sf.food.nutritionalInfo.vitaminC * multiplier).toFixed(1)}mg</span> : null}
                                  {sf.food.nutritionalInfo.vitaminA ? <span>🥕 Vit. A: {(sf.food.nutritionalInfo.vitaminA * multiplier).toFixed(1)}mcg</span> : null}
                                  {sf.food.nutritionalInfo.fiber ? <span>🌾 Fibra: {(sf.food.nutritionalInfo.fiber * multiplier).toFixed(1)}g</span> : null}
                                  {sf.food.nutritionalInfo.potassium ? <span>🍌 Potássio: {(sf.food.nutritionalInfo.potassium * multiplier).toFixed(0)}mg</span> : null}
                                  {sf.food.nutritionalInfo.magnesium ? <span>🔩 Magnésio: {(sf.food.nutritionalInfo.magnesium * multiplier).toFixed(0)}mg</span> : null}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {selectedFoods.length > 0 && (
                      <div style={{
                        padding: '12px',
                        background: 'var(--page-surface-hover)',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        border: '1px solid #0ea5e9'
                      }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Total da Refeição:</div>
                        <div style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>
                          <div>Calorias: {Math.round(selectedFoods.reduce((sum, sf) => {
                            const multiplier = sf.quantity / parseFloat(sf.food.servingSize as any);
                            return sum + (sf.food.nutritionalInfo.calories * multiplier);
                          }, 0))} kcal</div>
                          <div>
                            Proteína: {selectedFoods.reduce((sum, sf) => {
                              const multiplier = sf.quantity / parseFloat(sf.food.servingSize as any);
                              return sum + (sf.food.nutritionalInfo.protein * multiplier);
                            }, 0).toFixed(1)}g |
                            Carboidratos: {selectedFoods.reduce((sum, sf) => {
                              const multiplier = sf.quantity / parseFloat(sf.food.servingSize as any);
                              return sum + (sf.food.nutritionalInfo.carbs * multiplier);
                            }, 0).toFixed(1)}g |
                            Gorduras: {selectedFoods.reduce((sum, sf) => {
                              const multiplier = sf.quantity / parseFloat(sf.food.servingSize as any);
                              return sum + (sf.food.nutritionalInfo.fats * multiplier);
                            }, 0).toFixed(1)}g
                          </div>
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={handleCreateMeal}
                      disabled={!newMealName.trim() || selectedFoods.length === 0}
                      style={{ width: '100%' }}
                    >
                      Salvar Refeição
                    </Button>
                  </>
                )}
              </div>
            )}

            {activeTab === 'meals' && (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {loading ? (
                  <p>Carregando refeições...</p>
                ) : filteredMeals.length === 0 ? (
                  <p style={{ textAlign: 'center', color: 'var(--page-text-muted)', padding: '20px' }}>
                    Nenhuma refeição salva ainda. Adicione alimentos e salve como refeição.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {filteredMeals.map(meal => (
                      <div
                        key={meal.id}
                        style={{
                          padding: '16px',
                          background: favoriteMeals.has(meal.id) ? 'rgba(234, 179, 8, 0.16)' : 'var(--page-surface-hover)',
                          borderRadius: '8px',
                          border: `1px solid ${favoriteMeals.has(meal.id) ? '#fbbf24' : 'var(--page-border)'}`
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                          <div style={{ fontWeight: 600 }}>{meal.name}</div>
                          <button
                            type="button"
                            onClick={() => toggleFavorite(meal.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '20px'
                            }}
                            title={favoriteMeals.has(meal.id) ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                          >
                            {favoriteMeals.has(meal.id) ? '⭐' : '☆'}
                          </button>
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginBottom: '8px' }}>
                          {meal.foods.map(f => `${f.foodName} (${f.quantity}${f.unit})`).join(', ')}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--page-text-muted)', marginBottom: '12px' }}>
                          <div>Calorias: {meal.totalCalories} kcal</div>
                          <div>Proteína: {meal.totalProtein}g | Carboidratos: {meal.totalCarbs}g | Gorduras: {meal.totalFats}g</div>
                        </div>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            onAddMeal(meal);
                            alert(`Refeição "${meal.name}" adicionada!`);
                          }}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                        >
                          Usar esta Refeição
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Modal de Seleção de Dia e Refeição */}
      <Modal
        isOpen={showMealSelectorModal}
        onClose={() => {
          setShowMealSelectorModal(false);
          setSelectedFoodForMeal(null);
          setSelectedQuantity('');
        }}
        title="Adicionar Alimento à Refeição"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowMealSelectorModal(false);
                setSelectedFoodForMeal(null);
                setSelectedQuantity('');
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (selectedFoodForMeal && onAddFoodToMeal) {
                  const quantity = parseFloat(selectedQuantity) || (selectedFoodForMeal.servingSize as number);
                  onAddFoodToMeal(selectedFoodForMeal, selectedDay, selectedMeal, quantity);
                  setShowMealSelectorModal(false);
                  setSelectedFoodForMeal(null);
                  setSelectedQuantity('');
                  alert(`${selectedFoodForMeal.name} adicionado a ${MEAL_LABELS[selectedMeal]} de ${DAY_LABELS[selectedDay]}!`);
                }
              }}
              disabled={!selectedFoodForMeal || !selectedQuantity}
            >
              Adicionar
            </Button>
          </>
        }
      >
        <div style={{ padding: '16px' }}>
          {selectedFoodForMeal && (
            <>
              <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--page-surface-hover)', borderRadius: '8px' }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>{selectedFoodForMeal.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>
                  {selectedFoodForMeal.servingSize} {selectedFoodForMeal.servingUnit}
                </div>
              </div>

              <label className="ns-field">
                <span>Dia da Semana *</span>
                <select
                  className="ns-input"
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(e.target.value as DayKey)}
                >
                  {(Object.keys(DAY_LABELS) as DayKey[]).map((day) => (
                    <option key={day} value={day}>
                      {DAY_LABELS[day]}
                    </option>
                  ))}
                </select>
              </label>

              <label className="ns-field" style={{ marginTop: '12px' }}>
                <span>Tipo de Refeição *</span>
                <select
                  className="ns-input"
                  value={selectedMeal}
                  onChange={(e) => setSelectedMeal(e.target.value as MealKey)}
                >
                  {(Object.keys(MEAL_LABELS) as MealKey[]).map((meal) => (
                    <option key={meal} value={meal}>
                      {MEAL_LABELS[meal]}
                    </option>
                  ))}
                </select>
              </label>

              <label className="ns-field" style={{ marginTop: '12px' }}>
                <span>Quantidade *</span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="number"
                    className="ns-input"
                    value={selectedQuantity}
                    onChange={(e) => setSelectedQuantity(e.target.value)}
                    min="0"
                    step="0.1"
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>
                    {selectedFoodForMeal.servingUnit}
                  </span>
                </div>
                <small style={{ display: 'block', marginTop: '4px', color: 'var(--page-text-muted)', fontSize: '11px' }}>
                  Quantidade padrão: {selectedFoodForMeal.servingSize} {selectedFoodForMeal.servingUnit}
                </small>
              </label>
            </>
          )}
        </div>
      </Modal>

      {/* Modal de Criação de Alimento Personalizado */}
      <Modal
        isOpen={showAddCustomFoodModal}
        onClose={() => setShowAddCustomFoodModal(false)}
        title="Adicionar Alimento Personalizado"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAddCustomFoodModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateCustomFood}
              disabled={!newCustomFood.name || !newCustomFood.servingSize}
            >
              Criar Alimento
            </Button>
          </>
        }
      >
        <div style={{ padding: '16px', maxHeight: '400px', overflowY: 'auto' }}>
          <label className="ns-field" style={{ marginBottom: '12px' }}>
            <span>Nome do Alimento *</span>
            <input
              type="text"
              className="ns-input"
              value={newCustomFood.name}
              onChange={(e) => setNewCustomFood({ ...newCustomFood, name: e.target.value })}
              placeholder="Ex: Minha Barra de Proteína"
            />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <label className="ns-field">
              <span>Porção (g/ml) *</span>
              <input
                type="number"
                className="ns-input"
                value={newCustomFood.servingSize}
                onChange={(e) => setNewCustomFood({ ...newCustomFood, servingSize: e.target.value })}
              />
            </label>
            <label className="ns-field">
              <span>Unidade</span>
              <select
                className="ns-input"
                value={newCustomFood.servingUnit}
                onChange={(e) => setNewCustomFood({ ...newCustomFood, servingUnit: e.target.value })}
              >
                <option value="g">Gramas (g)</option>
                <option value="ml">Mililitros (ml)</option>
                <option value="unit">Unidade</option>
              </select>
            </label>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--page-text-primary)' }}>Informações Nutricionais (por porção)</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
              <label className="ns-field">
                <span>Calorias (kcal)</span>
                <input
                  type="number"
                  className="ns-input"
                  value={newCustomFood.calories}
                  onChange={(e) => setNewCustomFood({ ...newCustomFood, calories: e.target.value })}
                />
              </label>
              <label className="ns-field">
                <span>Proteínas (g)</span>
                <input
                  type="number"
                  className="ns-input"
                  value={newCustomFood.protein}
                  onChange={(e) => setNewCustomFood({ ...newCustomFood, protein: e.target.value })}
                />
              </label>
              <label className="ns-field">
                <span>Carboidratos (g)</span>
                <input
                  type="number"
                  className="ns-input"
                  value={newCustomFood.carbs}
                  onChange={(e) => setNewCustomFood({ ...newCustomFood, carbs: e.target.value })}
                />
              </label>
              <label className="ns-field">
                <span>Gorduras (g)</span>
                <input
                  type="number"
                  className="ns-input"
                  value={newCustomFood.fats}
                  onChange={(e) => setNewCustomFood({ ...newCustomFood, fats: e.target.value })}
                />
              </label>
            </div>
          </div>

          <label className="ns-field">
            <span>Categoria</span>
            <select
              className="ns-input"
              value={newCustomFood.category}
              onChange={(e) => setNewCustomFood({ ...newCustomFood, category: e.target.value })}
            >
              {CATEGORIES.filter(c => c.id !== 'all').map(cat => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
              <option value="other">Outro</option>
            </select>
          </label>
        </div>
      </Modal>
    </>
  );
};
